import React, { useMemo } from "react";
import { Search, Plus, Lock, Check, X, Info, Trash2 } from "lucide-react";

export const ColumnCatalogPanel = ({
  catalog,
  filteredTypes = [],
  selectedTypes = {},
  customColumns = [],
  searchQuery,
  setSearchQuery,
  kindFilter,
  setKindFilter,
  onToggleType,
  onToggleTypeVariant,
  onToggleGlobalVariant,
  onToggleGroup,
  onToggleAllFiltered,
  onOpenAddCustom,
  onRemoveCustomColumn,
  currentTheme,
  theme,
  isRTL,
  t,
  screenSize,
}) => {
  const isMobile = screenSize === "xs" || screenSize === "sm";

  // Check state of global variants
  const globalVariantStates = useMemo(() => {
    const states = { net: false, gross: false, days_net: false, days_gross: false };
    Object.values(selectedTypes).forEach((vList) => {
      vList.forEach((v) => {
        if (states[v] !== undefined) states[v] = true;
      });
    });
    return states;
  }, [selectedTypes]);

  // Group filtered types into Allowances and Deductions
  const allowanceTypes = useMemo(
    () => filteredTypes.filter((t) => t.kind === "allowance"),
    [filteredTypes]
  );
  const deductionTypes = useMemo(
    () => filteredTypes.filter((t) => t.kind === "deduction"),
    [filteredTypes]
  );

  // Tri-state helper for group headers
  const getGroupState = (typesList) => {
    if (typesList.length === 0) return "none";
    let selectedCount = 0;
    typesList.forEach((t) => {
      const vars = selectedTypes[t.key] || [];
      if (vars.length > 0) selectedCount++;
    });
    if (selectedCount === 0) return "none";
    if (selectedCount === typesList.length) return "all";
    return "some";
  };

  const allowanceState = getGroupState(allowanceTypes);
  const deductionState = getGroupState(deductionTypes);

  // Custom columns grouped
  const customAllowances = customColumns.filter((c) => c.kind === "allowance");
  const customDeductions = customColumns.filter((c) => c.kind === "deduction");

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        overflowY: "auto",
        paddingRight: isRTL ? 0 : "8px",
        paddingLeft: isRTL ? "8px" : 0,
      }}
    >
      {/* Search Bar & Add Custom Column Button */}
      <div
        style={{
          display: "flex",
          gap: "10px",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: "220px",
            position: "relative",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Search
            size={16}
            style={{
              position: "absolute",
              left: isRTL ? "auto" : "12px",
              right: isRTL ? "12px" : "auto",
              color: currentTheme.colors.textMuted,
            }}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("searchColumns") || "Search columns…"}
            style={{
              width: "100%",
              padding: "9px 34px",
              paddingLeft: isRTL ? "12px" : "36px",
              paddingRight: isRTL ? "36px" : "12px",
              borderRadius: "10px",
              border: `1px solid ${currentTheme.colors.border}`,
              background: currentTheme.colors.inputBackground,
              color: currentTheme.colors.textPrimary,
              fontSize: "0.85rem",
              outline: "none",
            }}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              style={{
                position: "absolute",
                right: isRTL ? "auto" : "10px",
                left: isRTL ? "10px" : "auto",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: currentTheme.colors.textMuted,
                display: "flex",
                alignItems: "center",
              }}
            >
              <X size={14} />
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={onOpenAddCustom}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "9px 14px",
            borderRadius: "10px",
            border: `1px solid ${currentTheme.colors.primary}`,
            background: `${currentTheme.colors.primary}12`,
            color: currentTheme.colors.primary,
            fontSize: "0.82rem",
            fontWeight: 600,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          <Plus size={15} />
          <span>{t("addColumn") || "Add column"}</span>
        </button>
      </div>

      {/* Filter Row: Kind Pills & Global Variant Toggles */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          padding: "10px 14px",
          borderRadius: "12px",
          background: theme === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
          border: `1px solid ${currentTheme.colors.borderLight}`,
        }}
      >
        {/* Kind selector */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span style={{ fontSize: "0.78rem", fontWeight: 600, color: currentTheme.colors.textMuted }}>
            {t("customColumnKind") || "Kind"}:
          </span>
          {[
            { id: "all", label: t("kindAll") || "All" },
            { id: "allowance", label: t("kindAllowances") || "Allowances" },
            { id: "deduction", label: t("kindDeductions") || "Deductions" },
          ].map((k) => (
            <button
              key={k.id}
              type="button"
              onClick={() => setKindFilter(k.id)}
              style={{
                padding: "4px 10px",
                borderRadius: "20px",
                border: kindFilter === k.id ? `1px solid ${currentTheme.colors.primary}` : `1px solid ${currentTheme.colors.borderLight}`,
                background: kindFilter === k.id ? currentTheme.colors.primary : "transparent",
                color: kindFilter === k.id ? "#fff" : currentTheme.colors.textSecondary,
                fontSize: "0.76rem",
                fontWeight: kindFilter === k.id ? 600 : 400,
                cursor: "pointer",
              }}
            >
              {k.label}
            </button>
          ))}
        </div>

        {/* Global variant toggles */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
          <span style={{ fontSize: "0.78rem", fontWeight: 600, color: currentTheme.colors.textMuted }}>
            {t("showVariants") || "Show"}:
          </span>
          {[
            { key: "net", label: t("variantNet") || "Net" },
            { key: "gross", label: t("variantGross") || "Gross" },
            { key: "days_net", label: t("variantDaysNet") || "Days Net" },
            { key: "days_gross", label: t("variantDaysGross") || "Days Gross" },
          ].map((v) => {
            const isActive = globalVariantStates[v.key];
            return (
              <button
                key={v.key}
                type="button"
                onClick={() => onToggleGlobalVariant(v.key)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "4px 8px",
                  borderRadius: "6px",
                  border: isActive ? `1px solid ${currentTheme.colors.primary}` : `1px solid ${currentTheme.colors.border}`,
                  background: isActive ? `${currentTheme.colors.primary}18` : "transparent",
                  color: isActive ? currentTheme.colors.primary : currentTheme.colors.textMuted,
                  fontSize: "0.74rem",
                  fontWeight: isActive ? 600 : 400,
                  cursor: "pointer",
                }}
              >
                {isActive && <Check size={11} strokeWidth={3} />}
                <span>{v.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Filter summary + Select/Clear all buttons */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "0.78rem",
          color: currentTheme.colors.textSecondary,
          padding: "0 4px",
        }}
      >
        <div>
          <span>
            {filteredTypes.length} {t("kindAll") ? "types matching filter" : "types"}
          </span>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            type="button"
            onClick={() => onToggleAllFiltered(true, filteredTypes)}
            style={{
              background: "transparent",
              border: "none",
              color: currentTheme.colors.primary,
              fontSize: "0.78rem",
              fontWeight: 600,
              cursor: "pointer",
              padding: 0,
            }}
          >
            {t("selectAllFiltered") || "Select all"}
          </button>
          <span>•</span>
          <button
            type="button"
            onClick={() => onToggleAllFiltered(false, filteredTypes)}
            style={{
              background: "transparent",
              border: "none",
              color: currentTheme.colors.textMuted,
              fontSize: "0.78rem",
              cursor: "pointer",
              padding: 0,
            }}
          >
            {t("clearAllSelection") || "Clear all"}
          </button>
        </div>
      </div>

      {/* 🔒 REQUIRED (Locked) Section */}
      <div
        style={{
          border: `1px solid ${theme === "dark" ? "rgba(30, 78, 120, 0.4)" : "#DDEBF7"}`,
          borderRadius: "10px",
          background: theme === "dark" ? "rgba(30, 78, 120, 0.15)" : "#F4F8FC",
          padding: "10px 14px",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.76rem", fontWeight: 700, color: theme === "dark" ? "#90cdf4" : "#1F4E78", textTransform: "uppercase" }}>
          <Lock size={12} />
          <span>{t("requiredColumns") || "REQUIRED COLUMNS"}</span>
          <span style={{ fontSize: "0.7rem", fontWeight: 400, opacity: 0.8 }}>({t("alwaysIncluded") || "Always included"})</span>
        </div>
        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
          {(catalog.locked || []).map((c) => (
            <div
              key={c.key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "0.82rem",
                color: currentTheme.colors.textPrimary,
                fontWeight: 600,
              }}
            >
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  borderRadius: "4px",
                  background: currentTheme.colors.primary,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Check size={12} strokeWidth={3} />
              </div>
              <span>{c.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ▮ ALLOWANCES Section */}
      {(kindFilter === "all" || kindFilter === "allowance") && (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {/* Group Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "6px 8px",
              borderBottom: `2px solid ${theme === "dark" ? "rgba(0, 97, 0, 0.5)" : "#C6EFCE"}`,
            }}
          >
            <div
              onClick={() => onToggleGroup("allowance", allowanceState !== "all")}
              style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}
            >
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  borderRadius: "4px",
                  border: `1.5px solid ${currentTheme.colors.primary}`,
                  background: allowanceState === "all" ? currentTheme.colors.primary : "transparent",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {allowanceState === "all" && <Check size={12} strokeWidth={3} />}
                {allowanceState === "some" && (
                  <div style={{ width: "8px", height: "2.5px", background: currentTheme.colors.primary }} />
                )}
              </div>
              <span style={{ fontSize: "0.82rem", fontWeight: 700, color: theme === "dark" ? "#86efac" : "#006100" }}>
                {t("kindAllowances") || "ALLOWANCES"} ({allowanceTypes.length + customAllowances.length})
              </span>
            </div>
          </div>

          {/* Allowance Type Rows */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {allowanceTypes.map((typeDef) => (
              <TypeRow
                key={typeDef.key}
                typeDef={typeDef}
                selectedVariants={selectedTypes[typeDef.key] || []}
                onToggleType={onToggleType}
                onToggleTypeVariant={onToggleTypeVariant}
                linkedQuantities={(typeDef.linkedQuantities || []).map((lq) => ({
                  ...lq,
                  selectedVariants: selectedTypes[lq.targetKey] || [],
                }))}
                currentTheme={currentTheme}
                theme={theme}
                isRTL={isRTL}
                t={t}
                isMobile={isMobile}
              />
            ))}

            {/* Custom Allowances */}
            {customAllowances.map((c, idx) => (
              <CustomColumnRow
                key={c.key || idx}
                col={c}
                onRemove={() => onRemoveCustomColumn(customColumns.indexOf(c))}
                currentTheme={currentTheme}
                theme={theme}
                t={t}
              />
            ))}
          </div>
        </div>
      )}

      {/* ▮ DEDUCTIONS Section */}
      {(kindFilter === "all" || kindFilter === "deduction") && (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "8px" }}>
          {/* Group Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "6px 8px",
              borderBottom: `2px solid ${theme === "dark" ? "rgba(156, 0, 6, 0.5)" : "#FFC7CE"}`,
            }}
          >
            <div
              onClick={() => onToggleGroup("deduction", deductionState !== "all")}
              style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}
            >
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  borderRadius: "4px",
                  border: `1.5px solid ${currentTheme.colors.primary}`,
                  background: deductionState === "all" ? currentTheme.colors.primary : "transparent",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {deductionState === "all" && <Check size={12} strokeWidth={3} />}
                {deductionState === "some" && (
                  <div style={{ width: "8px", height: "2.5px", background: currentTheme.colors.primary }} />
                )}
              </div>
              <span style={{ fontSize: "0.82rem", fontWeight: 700, color: theme === "dark" ? "#fca5a5" : "#9C0006" }}>
                {t("kindDeductions") || "DEDUCTIONS"} ({deductionTypes.length + customDeductions.length})
              </span>
            </div>
          </div>

          {/* Deduction Type Rows */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {deductionTypes.map((typeDef) => (
              <TypeRow
                key={typeDef.key}
                typeDef={typeDef}
                selectedVariants={selectedTypes[typeDef.key] || []}
                onToggleType={onToggleType}
                onToggleTypeVariant={onToggleTypeVariant}
                linkedQuantities={(typeDef.linkedQuantities || []).map((lq) => ({
                  ...lq,
                  selectedVariants: selectedTypes[lq.targetKey] || [],
                }))}
                currentTheme={currentTheme}
                theme={theme}
                isRTL={isRTL}
                t={t}
                isMobile={isMobile}
              />
            ))}

            {/* Custom Deductions */}
            {customDeductions.map((c, idx) => (
              <CustomColumnRow
                key={c.key || idx}
                col={c}
                onRemove={() => onRemoveCustomColumn(customColumns.indexOf(c))}
                currentTheme={currentTheme}
                theme={theme}
                t={t}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredTypes.length === 0 && (
        <div
          style={{
            padding: "36px",
            textAlign: "center",
            color: currentTheme.colors.textMuted,
            fontSize: "0.88rem",
          }}
        >
          {t("noColumnsMatch") || "No columns match"} "<strong>{searchQuery}</strong>"
        </div>
      )}
    </div>
  );
};

// Row component for a single standard catalog type
const TypeRow = ({
  typeDef,
  selectedVariants = [],
  onToggleType,
  onToggleTypeVariant,
  linkedQuantities = [],
  currentTheme,
  theme,
  isRTL,
  t,
  isMobile,
}) => {
  const isSelected = selectedVariants.length > 0;
  const isAllVariantsSelected = selectedVariants.length === (typeDef.availableVariants?.length || 2);
  const isQuantityOnly = typeDef.availableVariants?.length === 2 && !typeDef.availableVariants.includes("days_net");

  // Border color based on kind
  const isAllowance = typeDef.kind === "allowance";
  const borderSwatch = isAllowance ? "#C6EFCE" : "#FFC7CE";

  const displayName = (typeDef.labelKey && t(typeDef.labelKey)) || typeDef.label || typeDef.key;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: isMobile ? "wrap" : "nowrap",
        padding: "8px 12px",
        borderRadius: "8px",
        background: isSelected
          ? theme === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.015)"
          : "transparent",
        borderLeft: isRTL ? "none" : `3px solid ${borderSwatch}`,
        borderRight: isRTL ? `3px solid ${borderSwatch}` : "none",
        gap: "10px",
        transition: "background 0.1s ease",
      }}
    >
      {/* Type checkbox + name */}
      <div
        onClick={() => onToggleType(typeDef.key)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          cursor: "pointer",
          flex: 1,
          minWidth: "160px",
        }}
      >
        <div
          style={{
            width: "16px",
            height: "16px",
            borderRadius: "4px",
            border: `1.5px solid ${isSelected ? currentTheme.colors.primary : currentTheme.colors.border}`,
            background: isSelected ? `${currentTheme.colors.primary}18` : "transparent",
            color: currentTheme.colors.primary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isAllVariantsSelected && <Check size={12} strokeWidth={3.5} />}
          {isSelected && !isAllVariantsSelected && (
            <div style={{ width: "7px", height: "2px", background: currentTheme.colors.primary }} />
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span
            style={{
              fontSize: "0.82rem",
              fontWeight: isSelected ? 600 : 400,
              color: isSelected ? currentTheme.colors.textPrimary : currentTheme.colors.textSecondary,
            }}
          >
            {displayName}
          </span>
          {isQuantityOnly && (
            <span
              title={t("quantityColumnNotice") || "Quantity column (hours/days)"}
              style={{
                fontSize: "0.68rem",
                color: currentTheme.colors.textMuted,
                background: `${currentTheme.colors.border}44`,
                padding: "1px 4px",
                borderRadius: "4px",
              }}
            >
              qty
            </span>
          )}
        </div>
      </div>

      {/* Variant pills — standard Net/Gross/Days pair plus, for merged
          old-style types like Overtime, the linked columns' Net/Gross pills
          right beside them in the same row (e.g. Day Net, Day Gross, Hours
          Net, Hours Gross), instead of a separate wrapped-below sub-row. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "6px",
        }}
      >
        {[
          { key: "net", full: t("variantNet") || "Net" },
          { key: "gross", full: t("variantGross") || "Gross" },
          { key: "days_net", full: t("variantDaysNet") || "Days Net" },
          { key: "days_gross", full: t("variantDaysGross") || "Days Gross" },
        ].map((v) => {
          const isOffered = typeDef.availableVariants?.includes(v.key);
          const isVariantActive = selectedVariants.includes(v.key);
          const isDaysSlot = v.key === "days_net" || v.key === "days_gross";

          // Merged types (Overtime, Penalty Deduction, Late Deduction, ...)
          // surface their days/hours via linkedQuantities pills instead —
          // skip the "not applicable" dash for those two slots entirely.
          if (!isOffered && isDaysSlot && linkedQuantities.length > 0) {
            return null;
          }

          if (!isOffered) {
            return (
              <span
                key={v.key}
                title="Not applicable for quantity-based column"
                style={{
                  width: "82px",
                  textAlign: "center",
                  fontSize: "0.72rem",
                  color: currentTheme.colors.textMuted,
                  opacity: 0.35,
                  padding: "4px 0",
                }}
              >
                —
              </span>
            );
          }

          return (
            <button
              key={v.key}
              type="button"
              title={v.full}
              onClick={() => onToggleTypeVariant(typeDef.key, v.key)}
              style={{
                width: "82px",
                whiteSpace: "nowrap",
                padding: "4px 6px",
                borderRadius: "5px",
                border: isVariantActive ? `1px solid ${currentTheme.colors.primary}` : `1px solid ${currentTheme.colors.borderLight}`,
                background: isVariantActive ? currentTheme.colors.primary : "transparent",
                color: isVariantActive ? "#fff" : currentTheme.colors.textMuted,
                fontSize: "0.72rem",
                fontWeight: isVariantActive ? 600 : 400,
                cursor: "pointer",
                transition: "all 0.1s ease",
              }}
            >
              {v.full}
            </button>
          );
        })}

        {linkedQuantities.map((lq) =>
          [
            { variant: "net", full: t("variantNet") || "Net" },
            { variant: "gross", full: t("variantGross") || "Gross" },
          ].map((v) => {
            const isActive = lq.selectedVariants.includes(v.variant);
            return (
              <button
                key={`${lq.key}_${v.variant}`}
                type="button"
                title={`${lq.targetLabel} (${v.full})`}
                onClick={() => onToggleTypeVariant(lq.targetKey, v.variant)}
                style={{
                  width: "82px",
                  whiteSpace: "nowrap",
                  padding: "4px 6px",
                  borderRadius: "5px",
                  border: isActive ? `1px solid ${currentTheme.colors.primary}` : `1px solid ${currentTheme.colors.borderLight}`,
                  background: isActive ? currentTheme.colors.primary : "transparent",
                  color: isActive ? "#fff" : currentTheme.colors.textMuted,
                  fontSize: "0.72rem",
                  fontWeight: isActive ? 600 : 400,
                  cursor: "pointer",
                  transition: "all 0.1s ease",
                }}
              >
                {lq.label} {v.full}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

// Row component for a custom user-added column
const CustomColumnRow = ({ col, onRemove, currentTheme, theme, t }) => {
  const isAllowance = col.kind === "allowance";
  const borderSwatch = isAllowance ? "#C6EFCE" : "#FFC7CE";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 12px",
        borderRadius: "8px",
        background: theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
        borderLeft: `3px solid ${borderSwatch}`,
        gap: "10px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span
          style={{
            fontSize: "0.68rem",
            fontWeight: 700,
            textTransform: "uppercase",
            background: `${currentTheme.colors.primary}22`,
            color: currentTheme.colors.primary,
            padding: "2px 6px",
            borderRadius: "4px",
          }}
        >
          {t("customBadge") || "Custom"}
        </span>
        <span style={{ fontSize: "0.82rem", fontWeight: 600, color: currentTheme.colors.textPrimary }}>
          {col.label}
        </span>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <div style={{ display: "flex", gap: "4px", fontSize: "0.72rem", color: currentTheme.colors.textSecondary }}>
          {(col.variants || []).map((v) => (
            <span
              key={v}
              style={{
                padding: "2px 6px",
                borderRadius: "4px",
                background: `${currentTheme.colors.border}44`,
              }}
            >
              {v}
            </span>
          ))}
        </div>
        <button
          type="button"
          onClick={onRemove}
          title="Remove column"
          style={{
            background: "transparent",
            border: "none",
            color: currentTheme.colors.danger,
            cursor: "pointer",
            padding: "2px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
};
